import { collection, limit, onSnapshot, orderBy, query, where, type Timestamp, type Unsubscribe } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { firestoreTimeToMillis } from '../utils/firestoreTime'

export type HuddleSessionParticipant = {
  uid: string
  displayName: string
  avatarUrl: string
}

export type HuddleSession = {
  id: string
  workspaceId: string
  channelId: string
  channelName: string
  hostUid: string
  startedAt: Timestamp | Date | null
  endedAt: Timestamp | Date | null
  participants: HuddleSessionParticipant[]
}

export const subscribeHuddleSessions = (
  workspaceId: string,
  onChange: (sessions: HuddleSession[]) => void,
  onError?: (error: Error) => void,
  scope?: { restricted: boolean; uid: string },
): Unsubscribe => {
  if (scope?.restricted) {
    const uid = scope.uid
    let disposed = false
    let roleChannelIds = new Set<string>()
    let scopeReadChannelIds = new Set<string>()
    const channelListeners = new Map<string, { unsub: Unsubscribe; sessions: HuddleSession[] }>()

    const emitMergedSessions = () => {
      if (disposed) return
      const byId = new Map<string, HuddleSession>()
      for (const entry of channelListeners.values()) {
        for (const session of entry.sessions) {
          byId.set(session.id, session)
        }
      }
      const merged = Array.from(byId.values())
      merged.sort((a, b) => firestoreTimeToMillis(b.startedAt) - firestoreTimeToMillis(a.startedAt))
      onChange(merged.slice(0, 100))
    }

    const syncChannelSubscriptions = () => {
      if (disposed) return
      const union = new Set<string>([...roleChannelIds, ...scopeReadChannelIds])
      const targetChannelIds = Array.from(union).slice(0, 30)
      const targetSet = new Set(targetChannelIds)

      for (const [chId, entry] of channelListeners.entries()) {
        if (!targetSet.has(chId)) {
          entry.unsub()
          channelListeners.delete(chId)
        }
      }

      for (const chId of targetChannelIds) {
        if (!channelListeners.has(chId)) {
          const unsub = onSnapshot(
            query(
              collection(firestoreDb, 'huddle_sessions'),
              where('workspaceId', '==', workspaceId),
              where('channelId', '==', chId),
              orderBy('startedAt', 'desc'),
              limit(20),
            ),
            (snapshot) => {
              if (disposed) return
              const entry = channelListeners.get(chId)
              if (entry) {
                entry.sessions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as HuddleSession)
                emitMergedSessions()
              }
            },
            (err) => onError?.(err),
          )
          channelListeners.set(chId, { unsub, sessions: [] })
        }
      }

      emitMergedSessions()
    }

    const channelsColl = collection(firestoreDb, 'workspaces', workspaceId, 'channels')

    const unsubRoles = onSnapshot(
      query(channelsColl, where(`roles.${uid}`, 'in', ['admin', 'member'])),
      (snapshot) => {
        if (disposed) return
        roleChannelIds = new Set(snapshot.docs.map((d) => d.id))
        syncChannelSubscriptions()
      },
      (err) => onError?.(err),
    )

    const unsubScopeRead = onSnapshot(
      query(channelsColl, where('scopeRead', 'array-contains', uid)),
      (snapshot) => {
        if (disposed) return
        scopeReadChannelIds = new Set(snapshot.docs.map((d) => d.id))
        syncChannelSubscriptions()
      },
      (err) => onError?.(err),
    )

    return () => {
      disposed = true
      unsubRoles()
      unsubScopeRead()
      for (const entry of channelListeners.values()) {
        entry.unsub()
      }
      channelListeners.clear()
    }
  }

  return onSnapshot(
    query(collection(firestoreDb, 'huddle_sessions'), where('workspaceId', '==', workspaceId), orderBy('startedAt', 'desc'), limit(100)),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as HuddleSession)),
    (error) => onError?.(error),
  )
}
